import { Stack, Typography } from "@mui/material";
import { SummaryCard, SummaryCardRow } from "Components/_Shared/SummaryCard";
import type { AssetAllocation, PortfolioAssetAllocationOverview } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import { GoalFulfillmentFilterType, useGoalsFulfillmentContext } from "./GoalsFulfillmentContext";
import { GoalsFulfillmentSidebarChart } from "./GoalsFulfillmentSidebarChart";
import { roundAllocationTo100Percent } from "./helpers";

type WrapperProps = {
  futureAmount: number;
};

type Props = WrapperProps & {
  overview: PortfolioAssetAllocationOverview;
};

const hasNonZeroAllocation = (assetAllocation: AssetAllocation): boolean => {
  return Object.values(assetAllocation).some((value) => value !== 0);
};

export const GoalsFulfillmentSidebarWrapper = ({ futureAmount }: WrapperProps) => {
  const { assetAllocationOverview } = useGoalsFulfillmentContext();

  if (!assetAllocationOverview) return null;

  return <GoalsFulfillmentSidebar overview={assetAllocationOverview} futureAmount={futureAmount} />;
};

export const GoalsFulfillmentSidebar = ({ overview, futureAmount }: Props) => {
  const { t } = useResource();
  const { isFilterSelected } = useGoalsFulfillmentContext();
  const shouldShowPortfolioDetail = isFilterSelected(GoalFulfillmentFilterType.PortfolioDetail);
  const shouldShowFutureAmount = isFilterSelected(GoalFulfillmentFilterType.FutureAmount);

  return (
    <Stack spacing={Spacing.L} sx={{ flex: 2 }} pt={Spacing.L}>
      <Stack spacing={Spacing.XXXL} pb={Spacing.XXXL} px={Spacing.L} pl={Spacing.XXL} sx={{ width: 400 }}>
        <Stack spacing={Spacing.L}>
          <Stack direction="column" spacing={Spacing.L}>
            <Stack>
              <Typography variant="subtitle1" fontWeight={600} display="flex" alignItems="center">
                {t(Resources.AnalysisDetail.GoalsFulfillment.CurrentSolutionOverview)}
              </Typography>

              <SummaryCard variant="list">
                <SummaryCardRow
                  value={formatCurrencyWithoutCode(overview?.currentStateDto.balance.value ?? 0, 2)}
                  units={t(Resources.Common.CZKSuffix)}
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.CurrentBalance)}
                />
                <SummaryCardRow
                  value={formatCurrencyWithoutCode(overview?.currentStateDto.regularPayment.value ?? 0, 2)}
                  units={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.CurrentRegularDeposit)}
                />
              </SummaryCard>

              {shouldShowPortfolioDetail && hasNonZeroAllocation(overview.currentStateDto.assetAllocation) && (
                <GoalsFulfillmentSidebarChart allocation={roundAllocationTo100Percent(overview.currentStateDto.assetAllocation)} />
              )}
            </Stack>
          </Stack>

          <Stack direction="column" spacing={Spacing.S}>
            <Typography variant="subtitle1" fontWeight={600} display="flex" alignItems="center">
              {t(Resources.AnalysisDetail.GoalsFulfillment.ProposedSolutionOverview)}
            </Typography>

            <SummaryCard variant="list">
              <SummaryCardRow
                value={formatCurrencyWithoutCode(overview?.proposedStateDto.balance.value ?? 0, 2)}
                units={t(Resources.Common.CZKSuffix)}
                label={t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.NewBalance)}
              />
              <SummaryCardRow
                value={formatCurrencyWithoutCode(overview?.proposedStateDto.regularPayment.value ?? 0, 2)}
                units={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                label={t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.NewRegularDeposit)}
              />
              <SummaryCardRow
                value={formatCurrencyWithoutCode(overview?.requiredAmountCoverageGoals.value ?? 0, 2)}
                units={`${t(Resources.Common.CZKSuffix)}`}
                label={t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.NeededToSaveUp)}
              />
              {shouldShowFutureAmount && (
                <SummaryCardRow
                  value={formatCurrencyWithoutCode(futureAmount, 2)}
                  units={`${t(Resources.Common.CZKSuffix)}`}
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.FutureAmount)}
                />
              )}
            </SummaryCard>
            {shouldShowPortfolioDetail && hasNonZeroAllocation(overview.proposedStateDto.assetAllocation) && (
              <GoalsFulfillmentSidebarChart allocation={roundAllocationTo100Percent(overview.proposedStateDto.assetAllocation)} />
            )}
          </Stack>
        </Stack>

        <Stack direction="column" spacing={Spacing.L}>
          <Typography variant="subtitle1" fontWeight={600} display="flex" alignItems="center">
            {t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.PaymentStructure)}
          </Typography>

          <SummaryCard variant="list">
            <SummaryCardRow
              value={formatCurrencyWithoutCode(overview?.regularPaymentOverview.shortTermAmount.value ?? 0, 2)}
              units={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
              label={t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.ShortTermInvestment)}
            />
            <SummaryCardRow
              value={formatCurrencyWithoutCode(overview?.regularPaymentOverview.midTermAmount.value ?? 0, 2)}
              units={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
              label={t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.MidTermInvestment)}
            />
            <SummaryCardRow
              value={formatCurrencyWithoutCode(overview?.regularPaymentOverview.longTermAmount.value ?? 0, 2)}
              units={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
              label={t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.LongTermInvestment)}
            />
            <SummaryCardRow
              value={formatCurrencyWithoutCode(
                (overview?.regularPaymentOverview.shortTermAmount.value ?? 0) +
                  (overview?.regularPaymentOverview.midTermAmount.value ?? 0) +
                  (overview?.regularPaymentOverview.longTermAmount.value ?? 0),
                2,
              )}
              units={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
              label={t(Resources.AnalysisDetail.GoalsFulfillment.Sidebar.OverallInvestment)}
              labelFontWeight={600}
            />
          </SummaryCard>
        </Stack>
      </Stack>
    </Stack>
  );
};
